import { useState } from "react";
import {
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import {
    Help as HelpIcon,
    More as MoreIcon,
    ViewColumn as ViewColumnIcon,
    Cookie as CookieIcon,
    ExpandMore,
    PlayArrow,
    AccessTime as AccessTimeIcon,
    FilterList as FilterListIcon,
} from "@mui/icons-material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DraggableHelpDialog from "../../common/components/DraggableHelpDialog";

const RackPricingWeekendHolidayHelp = ({ open, onClose }) => {
    const [expandedPanel, setExpandedPanel] = useState("overview");

    const handlePanelChange = (panel) => (event, isExpanded) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    return (
        <DraggableHelpDialog dgKey="rack-pricing-wh-help" open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <HelpIcon color="primary" />
                    <Typography variant="h6">Rack Pricing Weekend/Holiday Help Guide</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box>
                    {/* Overview */}
                    <Accordion
                        expanded={expandedPanel === "overview"}
                        onChange={handlePanelChange("overview")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">System Overview</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography paragraph>
                                The Rack Pricing Weekend/Holiday module is designed for managing
                                rack pricing on weekends and holidays. Unlike the standard Rack Pricing,
                                this module allows you to select any date without restrictions and
                                work with pricing data for non-business days.
                            </Typography>
                            <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: "info.dark" }}>
                                    <strong>Key Difference:</strong> This module has no date restrictions,
                                    allowing you to price for weekends and holidays. When you proceed to
                                    Rack Confirmation Weekend/Holiday, you can specify a custom effective
                                    time for emails and DTN files.
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>

                    {/* Quick start guide */}
                    <Accordion
                        expanded={expandedPanel === "quickstart"}
                        onChange={handlePanelChange("quickstart")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">Quick Start Guide</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="subtitle1" gutterBottom>
                                <PlayArrow sx={{ verticalAlign: "middle", mr: 1 }} />
                                Get Started in 5 Steps:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="1. Select Effective Date"
                                        secondary="Use the date picker to choose any weekend or holiday date for rack pricing data. There are no date restrictions in this module."
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="2. Update Data"
                                        secondary="Click the 'Update' button to refresh pricing data and run rack contract pricing calculations."
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="3. Edit Pricing"
                                        secondary="Click directly on Margin or Rack cells to modify pricing values inline."
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="4. Search and Review"
                                        secondary="Use the search bar to find terminals, products, sales groups, or pricing formulas quickly."
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="5. Save and Proceed"
                                        secondary="Save your updated values, then proceed to Rack Confirmation Weekend/Holiday to send emails with a custom effective time."
                                    />
                                </ListItem>
                            </List>
                        </AccordionDetails>
                    </Accordion>

                    {/* Terminal Group Filter */}
                    <Accordion
                        expanded={expandedPanel === "terminalGroup"}
                        onChange={handlePanelChange("terminalGroup")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <FilterListIcon color="primary" />
                                <Typography variant="h6">Terminal Group Filter</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" component="div">
                                <strong>What is the Terminal Group Filter?</strong>
                                <br />
                                The Terminal Group dropdown allows you to filter and update pricing
                                for a specific group of terminals. This is useful when you need to
                                focus on a subset of terminals rather than the entire list.
                                <br /><br />
                                <strong>How to Use:</strong>
                                <br />
                                • Select a Terminal Group from the dropdown to filter the grid
                                <br />
                                • Click "Update by Terminal Group" to run pricing calculations for only that group
                                <br />
                                • Clear the selection to view all terminals again
                                <br /><br />
                                <strong>Benefits:</strong>
                                <br />
                                • Faster updates when working with a subset of terminals
                                <br />
                                • Easier to focus on specific market areas
                                <br />
                                • Reduces clutter when managing large terminal lists
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    {/* Effective Time in Confirmation */}
                    <Accordion
                        expanded={expandedPanel === "effectiveTime"}
                        onChange={handlePanelChange("effectiveTime")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <AccessTimeIcon color="primary" />
                                <Typography variant="h6">Effective Time in Confirmation</Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" component="div">
                                <strong>Custom Effective Time:</strong>
                                <br />
                                When you proceed to Rack Confirmation Weekend/Holiday, you can specify
                                a custom effective time that will appear in:
                                <br />
                                • Customer rack pricing emails
                                <br />
                                • DTN export files
                                <br /><br />
                                <strong>Why This Matters:</strong>
                                <br />
                                • Weekend and holiday pricing often has different effective times than regular business days
                                <br />
                                • You can communicate the exact time when pricing takes effect
                                <br />
                                • All terminals for the selected date will use the same effective time
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    {/* Data insights and popups */}
                    <Accordion
                        expanded={expandedPanel === "insights"}
                        onChange={handlePanelChange("insights")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">Data Insights & Popups</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <DriveFileRenameOutlineIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Rack Note"
                                                secondary="View detailed rack note with product and terminal information"
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <ReportProblemIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Outage Note"
                                                secondary="View detailed outage note with product and terminal information"
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <MoreIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="DTN Data"
                                                secondary="View DTN pricing data with ranking and terminal information"
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Column Management & Persistence */}
                    <Accordion
                        expanded={expandedPanel === "management"}
                        onChange={handlePanelChange("management")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">Column Management & Settings Persistence</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <ViewColumnIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Manage Columns"
                                                secondary="Click the three-dot menu on the Terminal column header (or other available columns) to access 'Manage Columns' and show/hide optional columns"
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <CookieIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Settings Saved Automatically"
                                                secondary="Your column visibility choices are automatically saved using browser cookies and persist across sessions"
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                                        Column Visibility Rules:
                                    </Typography>
                                    <Typography variant="body2" component="div" sx={{ pl: 2 }}>
                                        <strong>Always Visible (Cannot be hidden):</strong>
                                        <br />
                                        • Effective Date, Terminal, Description
                                        <br />
                                        • Cost, Margin, Suggested, Rack, Markup
                                        <br />
                                        • DTN
                                        <br /><br />
                                        <strong>Optional (Can be hidden):</strong>
                                        <br />
                                        • Prior Cost, Prior Markup, Prior Rack
                                        <br />
                                        • Outage Notes, Rack Notes
                                        <br />
                                        • Sales Group, Formula
                                    </Typography>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Column Descriptions */}
                    <Accordion
                        expanded={expandedPanel === "columnDescriptions"}
                        onChange={handlePanelChange("columnDescriptions")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">Column Descriptions</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" component="div">
                                <strong>Effective Date:</strong> The weekend/holiday date when the pricing becomes active
                                <br />
                                <strong>Terminal:</strong> The terminal location or market where the product is loaded
                                <br />
                                <strong>Description:</strong> The fuel product and any additional blend or octane details
                                <br />
                                <strong>Prior Cost:</strong> The cost from the previous pricing period
                                <br />
                                <strong>Prior Markup:</strong> The markup from the previous pricing period
                                <br />
                                <strong>Prior Rack:</strong> The rack price from the previous pricing period
                                <br />
                                <strong>Cost:</strong> The current cost for the product
                                <br />
                                <strong>Margin:</strong> The profit margin between cost and selling price
                                <br />
                                <strong>Suggested:</strong> The system-calculated suggested price (Cost + Margin with round-up logic)
                                <br />
                                <strong>Rack:</strong> The editable final rack selling price
                                <br />
                                <strong>Markup:</strong> The additional amount added to cost to determine the selling price
                                <br />
                                <strong>Sales Group:</strong> The sales group responsible for the account or pricing
                                <br />
                                <strong>Formula:</strong> The pricing formula applied (if any)
                                <br />
                                <strong>DTN:</strong> The DTN market pricing data used for comparison
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    {/* Pricing Calculations & Rounding Logic */}
                    <Accordion
                        expanded={expandedPanel === "calculations"}
                        onChange={handlePanelChange("calculations")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">Pricing Calculations & Rounding Logic</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" component="div">
                                <strong>Suggested Price Calculation:</strong>
                                <br />
                                • Formula: Cost + Margin = Suggested Price
                                <br />
                                • The system automatically calculates the suggested price by adding the margin to the cost
                                <br />
                                <br />
                                <strong>Rounding Logic:</strong>
                                <br />
                                • All suggested prices use round-up (ceiling) logic to the nearest 0.0005 (fourth decimal place)
                                <br />
                                • Mathematical formula: Math.ceil(value x 2000) / 2000
                                <br />
                                • Example: 2.1234 becomes 2.1235, and 2.1230 stays 2.1230
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    {/* Tips & Best Practices*/}
                    <Accordion
                        expanded={expandedPanel === "tips"}
                        onChange={handlePanelChange("tips")}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">Tips & Best Practices</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" component="div">
                                • This module allows selection of any date, including weekends and holidays
                                <br />
                                • Use the Terminal Group filter to focus on specific market areas
                                <br />
                                • Use "Update by Terminal Group" for faster updates on a subset of terminals
                                <br />
                                • Always update data before making pricing decisions
                                <br />
                                • Use the insights popups to understand market context
                                <br />
                                • Save changes frequently to avoid losing work
                                <br />
                                • Compare DTN prices to ensure competitive positioning
                                <br />
                                • Use the search function to quickly find specific terminals, products, sales groups, or formulas
                                <br />
                                • Proceed to Rack Confirmation Weekend/Holiday to send emails with a custom effective time
                                <br />
                                • Test in Development/Test environment first to verify pricing and email content
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </DraggableHelpDialog>
    );
};

export default RackPricingWeekendHolidayHelp;
